import { DOCTOR_PORTAL_PAGE, NOTIFICATIONS_PAGE } from '@app/routes/urls';
import { MenuStructure, RoutesStructure } from '@app/types/routing.types';

/**
 * Reduce the staff route structure to the doctor's own portal, plus the short
 * allow-list below.
 *
 * Written as "hide everything, then re-show the doctor's own" rather than as a
 * list of what to hide. The portal's routes default to `show: true` because it
 * was built for administrators, so an allow-list is the only shape that keeps a
 * page added next month out of a persona it was never designed for. The
 * alternative — enumerating admin pages to hide — is wrong the moment someone
 * adds one and does not think of the doctor.
 *
 * Returns a new structure; the caller's is left alone, because the same
 * structure object is memoised and handed to other consumers.
 */

type MutableRoute = { show?: boolean; routes?: Record<string, MutableRoute> };

const withVisibility = (route: MutableRoute, show: boolean): MutableRoute => ({
  ...route,
  ...(route.show === undefined ? {} : { show }),
  ...(route.routes
    ? {
        routes: Object.fromEntries(
          Object.entries(route.routes).map(([key, child]) => [key, withVisibility(child, show)])
        ),
      }
    : {}),
});

/**
 * Routes a doctor keeps although they live outside the portal.
 *
 * Deliberately a short, explicit list rather than a relaxation of the rule: the
 * default for anything new is still hidden. The notifications page is here
 * because the bell is in the header for every persona, and its "All
 * notifications" button pointed at a route the doctor could not open.
 *
 * withVisibility cascades a key's `show` into its whole subtree, so a future
 * route nested under NOTIFICATIONS_PAGE inherits visibility too without anyone
 * touching this file.
 */
const ALLOWED_OUTSIDE_THE_PORTAL: ReadonlySet<string> = new Set([NOTIFICATIONS_PAGE]);

export const restrictToDoctorPortal = (routes: RoutesStructure): RoutesStructure =>
  Object.fromEntries(
    Object.entries(routes as unknown as Record<string, MutableRoute>).map(([key, route]) => [
      key,
      withVisibility(route, key === DOCTOR_PORTAL_PAGE || ALLOWED_OUTSIDE_THE_PORTAL.has(key)),
    ])
  ) as unknown as RoutesStructure;

/**
 * The full paths the doctor's own routes occupy, read off the structure itself.
 *
 * Read rather than written down, so it cannot drift from the routes it is meant
 * to describe.
 */
export const doctorPortalPaths = (routes: RoutesStructure): Set<string> => {
  const paths = new Set<string>();
  const walk = (route: MutableRoute & { full_path?: string }) => {
    if (route.full_path) paths.add(route.full_path);
    Object.values(route.routes ?? {}).forEach((child) => walk(child as MutableRoute & { full_path?: string }));
  };
  const portal = (routes as unknown as Record<string, MutableRoute & { full_path?: string }>)[DOCTOR_PORTAL_PAGE];
  if (portal) walk(portal);
  return paths;
};

/**
 * Reduce the MENU to the doctor's own entries.
 *
 * `restrictToDoctorPortal` cannot do this on its own. It hides ROUTES, and the
 * menu is not built from them alone: fifteen entries in `getMenu` carry a
 * literal `show: true` - group headers like "Visits", and children such as the
 * service-request list - so they never consult a route's visibility and stayed
 * in the sidebar of a doctor who could not open any of them.
 *
 * Keyed on the doctor's own paths rather than on a list of what to remove, for
 * the reason the route version is: an entry added next month is then absent from
 * the doctor's menu by default instead of leaking into it. A group header keeps
 * no path of its own, so it drops out with its children - which is right, since
 * every group in this menu is a staff one.
 */
export const restrictMenuToDoctorPortal = (menu: MenuStructure, allowed: Set<string>): MenuStructure =>
  menu
    .map((item) => ({
      ...item,
      children: item.children ? restrictMenuToDoctorPortal(item.children, allowed) : undefined,
    }))
    .filter((item) => allowed.has(item.key) || (item.children?.length ?? 0) > 0);
